import {Injectable} from '@angular/core';
import {BaseApiService} from '../../common/base-api.service';
import {HttpClient} from '@angular/common/http';
import {SettingsProviderService} from '../../common/settings-provider.service';
import {Observable, of} from 'rxjs';
import {IObservation} from '../../message-models/observation.interface';
import {OperativeObservation} from '../../domain-model/operative-observation.class';
import {IOperativeObservation} from '../../message-models/operative-observation.interface';
import {IDocumentObservation} from '../../message-models/document-observation.interface';
import {ITrainingRecordObservation} from '../../message-models/training-record-observation.interface';
import {ICompanyObservation} from '../../message-models/company-observation.interface';
import {ObservationType, ObservationTypeString} from '../../domain-model/observation.class';
import {DocumentObservation} from '../../domain-model/document-observation.class';
import {TrainingRecordObservation} from '../../domain-model/training-record-observation.class';
import {CompanyObservation} from '../../domain-model/company-observation.classs';

import {IObservationSummary} from '../../message-models/observation-summary.interface';
import {switchMap} from 'rxjs/operators';
import { IObservationDocumentSummary } from '../../message-models/observation-document-summary.interface';


@Injectable({
  providedIn: 'root'
})
export class ObservationsService extends BaseApiService {
  constructor(http: HttpClient) {
    super(http, new SettingsProviderService('ecaApi'), 'Observations');
  }

  getAllObservations(): Observable<IObservation[]> {
    return this.http.post<IObservation[]>(this.settingsProvider.baseApiUrl + '', null);
  }

  getObservationById(observationId: string): Observable<IObservation> {
    return this.post<IObservation>('GetObservationById', { observationId : observationId });
  }

  getObservationSummaryById(observationId: string): Observable<IObservationSummary> {
    return this.post<IObservationSummary>('GetObservationSummaryById', { observationId: observationId });
  }

  createOrUpdateOperativeObservation(newObservation: IObservation, operativeId: string,companyId: string, operativeObservationId?: string) {
    const observation = newObservation as IObservation;
    const operativeObservation = new OperativeObservation({
      operativeId: operativeId,
      observation: observation,
      observationId: observation.id,
    } as IOperativeObservation);
    if(operativeObservationId){
      operativeObservation.id = operativeObservationId;
    }
    operativeObservation.observation.observationType = ObservationType.Operative;
    return this.post<IOperativeObservation>('CreateOrUpdateOperativeObservation', {companyId: companyId,operativeObservationInfo: operativeObservation as IOperativeObservation});
  }

  getOperativeObservations(operativeId: string, companyId: string): Observable<IObservationSummary[]> {
    return this.post<IObservationSummary[]>('GetOperativeObservations', {operativeId: operativeId, companyId: companyId});
  }

  getAllOperativeObservationsForCompany(companyId: string): Observable<IObservationSummary[]>{
    return this.post<IObservationSummary[]>('GetAllOperativeObservationsForCompany',{companyId: companyId});
  }

  deleteObservation(observationId: string, observationType: ObservationType, companyId: string): Observable<boolean>{

    switch (observationType){
      case  ObservationType.Operative: {
        return this.deleteOperativeObservation(observationId,companyId).pipe(switchMap(result => {
          return of(result);
        }));
      }
      case ObservationType.OperativeDocument: case ObservationType.TrainingRecordDocument: case ObservationType.CompanyDocument : {
        return this.deleteDocumentObservation(observationId,companyId).pipe(switchMap(result => {
          return of(result);
        }));
      }
      case ObservationType.TrainingRecord: {
        return this.deleteTrainingRecordObservation(observationId,companyId).pipe(switchMap(result => {
          return of(result);
        }));
      }
      case ObservationType.Company: {
        return  this.deleteCompanyObservation(observationId,companyId).pipe(switchMap(result => {
          return of(result);
        }));
      }
    }
  }

  deleteCompanyObservation(observationId: string, companyId: string): Observable<boolean> {
    return this.post<boolean>('DeleteCompanyObservation',{observationId: observationId, companyId: companyId});
  }

  deleteOperativeObservation(observationId: string, companyId: string): Observable<boolean> {
    return this.post<boolean>('DeleteOperativeObservation',{observationId: observationId, companyId: companyId});
  }

  deleteTrainingRecordObservation(observationId: string, companyId: string): Observable<boolean> {
    return this.post<boolean>('DeleteTrainingRecordObservation',{observationId: observationId, companyId: companyId});
  }

  deleteDocumentObservation(observationId: string, companyId: string): Observable<boolean> {
    return this.post<boolean>('DeleteDocumentObservation',{observationId: observationId, companyId: companyId});
  }

  createOrUpdateDocumentObservation(newObservation: IObservation, documentId: string, companyId: string, documentObservationId?: string) {
    const observation = newObservation as IObservation;
    let documentObservation = new DocumentObservation({
      documentId: documentId,
      observation: observation,
      observationId: observation.id,
    } as IDocumentObservation);
    if(documentObservationId){
      documentObservation.id = documentObservationId;
    }
    return this.post<IDocumentObservation>('CreateOrUpdateDocumentObservation',{companyId: companyId,documentObservationInfo: documentObservation as IDocumentObservation } );
  }

  getDocumentObservations(documentId: string, companyId: string): Observable<IObservationSummary[]> {
    return this.post<IObservationSummary[]>('GetDocumentObservations', {documentId: documentId,companyId: companyId});
  }

  createOrUpdateTrainingRecordObservation(newObservation: IObservation, trainingRecordId: string,companyId: string, trainingRecordObservationId?: string) {
    const observation = newObservation as IObservation;
    let trainingRecordObservation = new TrainingRecordObservation({
      trainingRecordId: trainingRecordId,
      observation: observation,
      observationId: observation.id,
    } as ITrainingRecordObservation);
    if(trainingRecordObservationId){
      trainingRecordObservation.id = trainingRecordObservationId;
    }
    trainingRecordObservation.observation.observationType = ObservationType.TrainingRecord;
    return this.post<ITrainingRecordObservation>('CreateOrUpdateTrainingRecordObservation',{companyId: companyId,trainingRecordObservationInfo: trainingRecordObservation as ITrainingRecordObservation } );
  }

  getTrainingRecordObservations(trainingRecordId: string, companyId: string ): Observable<IObservationSummary[]> {
    return this.post<IObservationSummary[]>('GetTrainingRecordObservations', {trainingRecordId: trainingRecordId,companyId: companyId});
  }

  createOrUpdateCompanyObservation(newObservation: IObservation, companyId: string, companyObservationId?: string) {
    const observation = newObservation as IObservation;
    observation.observationType = ObservationType.Company;
    let companyObservation = new CompanyObservation({
      companyId: companyId,
      observation: observation,
      observationId: observation.id,

    } as ICompanyObservation);
    if(companyObservationId){
      companyObservation.id = companyObservationId;
    }
    companyObservation.observation.observationType= ObservationType.Company;
    return this.post<ICompanyObservation>('CreateOrUpdateCompanyObservation', companyObservation as ICompanyObservation);
  }

  getCompanyObservations(companyId: string): Observable<IObservationSummary[]> {
    return this.post<IObservationSummary[]>('GetCompanyObservations', {companyId: companyId});
  }
  
  getRecentObservationsForCompany(companyId: string): Observable<IObservationSummary[]> {
    return this.post<IObservationSummary[]>('GetRecentObservationsForCompany', {companyId: companyId});
  }

  updateObservationSummaries(operativeObservationsSummary: Array<IObservationSummary> , observationEntity: any, companyName?: string) : Array<IObservationSummary>{
    const exists =  operativeObservationsSummary.findIndex(x => x.observationId === observationEntity.observationId) > -1;
    if(exists){
      operativeObservationsSummary.forEach(s=>{
        if(s.observationId === observationEntity.observationId){
          s.observationId = observationEntity.observationId;
          s.summaryInfo = observationEntity.observation.summaryInfo;
          s.notesAsHtml = observationEntity.observation.notesAsHtml;
          s.observedBy = observationEntity.observation.observedBy;
          s.observedOn = observationEntity.observation.observedOn;
          s.followUpOn = observationEntity.observation.followUpOn;
          s.observationType = observationEntity.observation.observationType;
        }
      })
    } else {
      let observationSummary = {
        observationId: observationEntity.observationId,
        summaryInfo: observationEntity.observation.summaryInfo,
        notesAsHtml: observationEntity.observation.notesAsHtml,
        observedBy: observationEntity.observation.observedBy,
        observedOn: observationEntity.observation.observedOn,
        followUpOn: observationEntity.observation.followUpOn,
        observationType: observationEntity.observation.observationType,
        operativeName: observationEntity.operativeName,
        companyName: companyName
      } as IObservationSummary;
      switch (observationEntity.observation.observationType) {
        case ObservationType.Operative:
          observationSummary.operativeObservationId = observationEntity.id;
          observationSummary.observationTypeString = ObservationTypeString[ObservationType.Operative];
          observationSummary.observationType = ObservationType.Operative;
          break;
        case ObservationType.TrainingRecordDocument:
          observationSummary.observationType = ObservationType.TrainingRecordDocument;
          observationSummary.observationTypeString = ObservationTypeString[ObservationType.TrainingRecordDocument];
          observationSummary.documentObservationId = observationEntity.id;
          break;
        case ObservationType.CompanyDocument:
          observationSummary.observationTypeString = ObservationTypeString[ObservationType.CompanyDocument];
          observationSummary.observationType = ObservationType.CompanyDocument;
          observationSummary.documentObservationId = observationEntity.id;
          break;
        case ObservationType.OperativeDocument:
          observationSummary.observationTypeString = ObservationTypeString[ObservationType.OperativeDocument];
          observationSummary.observationType = ObservationType.OperativeDocument;
          observationSummary.documentObservationId = observationEntity.id;
          break;
        case ObservationType.Company:
          observationSummary.observationTypeString = ObservationTypeString[ObservationType.Company];
          observationSummary.observationType = ObservationType.Company;
          observationSummary.companyObservationId = observationEntity.id;
          break;
        case ObservationType.TrainingRecord:
          observationSummary.observationTypeString = ObservationTypeString[ObservationType.TrainingRecord];
          observationSummary.observationType = ObservationType.TrainingRecord;
          observationSummary.trainingRecordObservationId = observationEntity.id;
          break;
      }
      operativeObservationsSummary?.push(observationSummary);
    }
    return operativeObservationsSummary;
  }
}
